import { useSortable } from '@dnd-kit/sortable';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Stack, Text } from '@sixui/ui';
import { iconButtonTokens } from '@sixui/ui/components/IconButton/IconButton.stylex';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import stylex from '@stylexjs/stylex';

import type { IContainerProps } from '~/components/utils/Container';
import type { IAny, IMaybeAsync } from '~/helpers/types';
import { css } from '~/helpers/css';

export type ITabsSettingsTabProps = IContainerProps & {
  id: string;
  icon: React.ReactNode;
  label: string;
  onDelete?: (event: React.MouseEvent<HTMLElement>) => IMaybeAsync<IAny>;
  active?: boolean;
};

type ISortableStyle = {
  transform?: string;
  transition?: string;
  zIndex?: number;
};

const styles = stylex.create({
  host: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.5rem',
    cursor: 'grab',
    flexGrow: 1,
    flexBasis: 0,
    color: {
      default: colorSchemeTokens.outline,
      ':hover': colorSchemeTokens.onSurface,
    },
  },
  host$active: {
    color: {
      default: colorSchemeTokens.primary,
      ':hover': colorSchemeTokens.primary,
    },
  },
  icon: {
    fontSize: '1.5rem',
  },
  actions: {
    position: 'absolute',
    top: -5,
    right: -5,
    zIndex: 1,
  },
  deleteButton: {
    [iconButtonTokens.iconColor]: colorSchemeTokens.primary,
    [iconButtonTokens.iconColor$hover]: colorSchemeTokens.error,
    [iconButtonTokens.iconColor$focus]: colorSchemeTokens.error,
    [iconButtonTokens.iconColor$pressed]: colorSchemeTokens.error,
  },
  sortable: (transform: string, transition: string, zIndex: number) => ({
    transform,
    transition,
    zIndex,
  }),
});

export const TabsSettingsTab: React.FC<ITabsSettingsTabProps> = (props) => {
  const { sx, id, icon, label, onDelete, active } = props;
  const { listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const sortableStyle: ISortableStyle = {
    transform: css.transform.toString(transform),
    transition,
    zIndex: isDragging ? 99 : undefined,
  };

  return (
    <Stack
      gap={2}
      sx={[
        styles.host,
        active && styles.host$active,
        styles.sortable(
          sortableStyle?.transform ?? 'none',
          sortableStyle?.transition ?? 'none',
          sortableStyle?.zIndex ?? 0,
        ),
        sx,
      ]}
      ref={setNodeRef}
      {...listeners}
    >
      <div {...stylex.props(styles.icon)}>{icon}</div>
      <Text variant="label" size="lg">
        {label}
      </Text>
      {onDelete ? (
        <div {...stylex.props(styles.actions)}>
          <IconButton
            sx={styles.deleteButton}
            icon={<FontAwesomeIcon icon={faTrashCan} />}
            onClick={onDelete}
          />
        </div>
      ) : null}
    </Stack>
  );
};
